import { Component, computed, effect, inject, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { finalize, map, Observable } from 'rxjs';
import { DyeingReprocessCommand, ReprocessCommand, ReprocessCommandHistory } from '@app/models/commands/reprocess-command';
import { DyeingMachine } from '@app/models/dyeing-dashboard/dyeing-machine';
import { Roll } from '@app/models/knitting/roll';
import { WorkOrder } from '@app/models/order/work-order';
import { DyeingColor } from '@app/models/production/dyeing/dyeing-color';
import { ProductionCard } from '@app/models/production/production-card';
import { ProductionCardStatus } from '@app/models/production/production-card';
import { BundleItem, BundleRoll, DyeingMode } from '@app/models/production/reprocess';
import { LabDipRequestProxyService } from '@app/proxies/item/lab-dip-request-proxy.service';
import { WorkOrderProxyService } from '@app/proxies/order/work-order.proxy.service';
import { DyeingMachineProxyService } from '@app/proxies/production/dyeing-machine.proxy.service';
import { ProductionCardsProxyService } from '@app/proxies/production/production-card.proxy.service';
import { ReprocessProxyService } from '@app/proxies/production/reprocess.proxy.service';
import { RollProxyService } from '@app/proxies/production/roll.proxy.service';
import { LocaleHelperService } from '@app/services/locale-helper.service';
import { LocationPathService } from '@app/services/location-path.service';
import { KnittingUtil } from '@app/utilities/knitting-util';
import { isInValidReprocessWarehouse } from '@app/utilities/reprocess-location.util';

@Component({
  selector: 'app-reprocess-creation',
  templateUrl: './reprocess-creation.component.html',
  styleUrl: './reprocess-creation.component.scss',
})
export class ReprocessCreationComponent implements OnInit {
  private readonly productionCardProxy = inject(ProductionCardsProxyService);
  private readonly rollProxy = inject(RollProxyService);
  private readonly dyeingMachineProxy = inject(DyeingMachineProxyService);
  private readonly reprocessProxy = inject(ReprocessProxyService);
  private readonly workOrderProxy = inject(WorkOrderProxyService);
  private readonly labDipRequestProxy = inject(LabDipRequestProxyService);
  private readonly localeHelperService = inject(LocaleHelperService);
  private readonly locationPathService = inject(LocationPathService);

  // Stepper state
  activeStep = signal<number>(1);

  // Production card search
  selectedProductionCard = signal<ProductionCard | null>(null);

  // Work order and color data for display
  workOrder = signal<WorkOrder | null>(null);
  color = signal<DyeingColor | null>(null);

  // Roll data
  rolls = signal<Roll[]>([]);
  selectedRolls = signal<Roll[]>([]);

  private readonly loadLocationPaths = effect(() => {
    this.locationPathService.load(this.rolls().map((roll) => roll.currentLocationCode));
  });
  readonly isWholeCardReprocessControl = new FormControl<boolean>(true, { nonNullable: true });
  readonly isWholeCardReprocess = toSignal(this.isWholeCardReprocessControl.valueChanges, { initialValue: true });
  showRollsTable = signal<boolean>(false);
  readonly rollNumberFilterControl = new FormControl<string>('', { nonNullable: true });
  readonly rollNumberFilter = toSignal(this.rollNumberFilterControl.valueChanges, { initialValue: '' });

  // Filtered rolls based on roll number filter
  filteredRolls = computed(() => {
    const filter = this.rollNumberFilter().toLowerCase().trim();
    if (!filter) return this.rolls();
    return this.rolls().filter((r) => r.number?.toLowerCase().includes(filter));
  });

  // Distinct locations from selected rolls
  distinctLocations = computed(() => {
    const locations = this.selectedRolls()
      .map((r) => r.currentLocationCode)
      .filter((loc): loc is string => !!loc);
    return [...new Set(locations)];
  });

  /** Rolls the API would reject: only Finishing and Rolling (or a sub-location of either) can be reprocessed. */
  invalidLocationRolls = computed(() => this.rolls().filter((r) => !isInValidReprocessWarehouse(r.currentLocationCode)));

  invalidLocationRollNumbers = computed(() => this.invalidLocationRolls().map((r) => r.number ?? ''));

  /** Non-empty only for a whole-card reprocess: the table stops the operator from picking these one by one. */
  selectedInvalidLocationRolls = computed(() => this.selectedRolls().filter((r) => !isInValidReprocessWarehouse(r.currentLocationCode)));

  // Dyeing configuration
  readonly includeDyeingControl = new FormControl<boolean>(false, { nonNullable: true });
  readonly includeDyeing = toSignal(this.includeDyeingControl.valueChanges, { initialValue: false });
  dyeingMode = signal<DyeingMode>('batching');
  dyeingMachines: DyeingMachine[] = [];
  readonly selectedDyeingMachineControl = new FormControl<DyeingMachine | null>(null);
  readonly selectedDyeingMachine = toSignal(this.selectedDyeingMachineControl.valueChanges, { initialValue: null });

  // Bundle state
  bundles: BundleItem[] = [];
  unassignedRolls: BundleRoll[] = [];
  selectedBundleId: string | null = null;
  readonly unassignedRollFilterControl = new FormControl<string>('', { nonNullable: true });
  readonly unassignedRollFilter = toSignal(this.unassignedRollFilterControl.valueChanges, { initialValue: '' });

  // Result state
  reprocessResult = signal<ReprocessCommandHistory | null>(null);
  resultProductionCard = signal<ProductionCard | null>(null);

  // Loading states
  isLoadingRolls = signal<boolean>(false);
  isSubmitting = signal<boolean>(false);

  ngOnInit(): void {
    this.loadDyeingMachines();
  }

  // ========== PRODUCTION CARD SEARCH ==========

  public fetchProductionCards = (query: string): Observable<ProductionCard[]> =>
    this.productionCardProxy.search({ productionCardNumber: query }).pipe(map((response) => response.data ?? []));

  onProductionCardSelected(productionCard: ProductionCard): void {
    this.selectedProductionCard.set(productionCard);
    this.loadRolls();
    this.loadWorkOrder(productionCard.workOrderReference);
    this.loadColor(productionCard.colorReference);
  }

  clearProductionCard(): void {
    this.selectedProductionCard.set(null);
    this.rolls.set([]);
    this.selectedRolls.set([]);
    this.workOrder.set(null);
    this.color.set(null);
  }

  // ========== WORK ORDER & COLOR LOADING ==========

  private loadWorkOrder(workOrderReference: string): void {
    if (!workOrderReference) {
      this.workOrder.set(null);
      return;
    }

    this.workOrderProxy.getById(workOrderReference).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.workOrder.set(response.data);
        }
      },
    });
  }

  private loadColor(colorReference: string | undefined): void {
    if (!colorReference) {
      this.color.set(null);
      return;
    }

    this.labDipRequestProxy.getColorsByIds([colorReference]).subscribe({
      next: (response) => {
        if (response.success && response.data && response.data.length > 0) {
          this.color.set(response.data[0]);
        }
      },
    });
  }

  // ========== ROLL LOADING ==========

  private loadRolls(): void {
    const pc = this.selectedProductionCard();
    if (!pc?.productionCardNumber) return;

    this.isLoadingRolls.set(true);
    this.rollProxy
      .getRollsCurrentlyLinkedToProductionCardNumber(pc.productionCardNumber)
      .pipe(finalize(() => this.isLoadingRolls.set(false)))
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.rolls.set(response.data);
            this.selectedRolls.set(this.isWholeCardReprocess() ? [...response.data] : []);
          }
        },
      });
  }

  // ========== DYEING MACHINES ==========

  private loadDyeingMachines(): void {
    this.dyeingMachineProxy.getAllMachines().subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.dyeingMachines = response.data;
        }
      },
    });
  }

  // ========== ROLL SELECTION ==========

  onWholeCardToggle(): void {
    if (this.isWholeCardReprocessControl.value) {
      this.selectedRolls.set([...this.rolls()]);
      this.showRollsTable.set(false);
    } else {
      // The excluded rolls' checkboxes are disabled, so they have to
      // leave the selection here or the operator would be unable to unblock the step.
      this.selectedRolls.update((selected) => selected.filter((r) => isInValidReprocessWarehouse(r.currentLocationCode)));
      this.showRollsTable.set(true);
    }
  }

  toggleRollsTable(): void {
    this.showRollsTable.update((v) => !v);
  }

  onRollSelectionChange(selection: Roll[]): void {
    this.selectedRolls.set(selection);

    const allSelected = selection.length === this.rolls().length && this.rolls().length > 0;
    if (!allSelected) {
      this.isWholeCardReprocessControl.setValue(false);
    }
  }

  getRollLocationLabel(roll: Roll): string {
    return this.locationPathService.label(
      roll.currentLocationCode,
      this.localeHelperService.translateWithFallback('Reprocess_UnknownLocation', 'unknown location'),
    );
  }

  getDistinctLocationsLabel(): string {
    return this.distinctLocations()
      .map((code) => this.locationPathService.label(code))
      .join(', ');
  }

  getInvalidLocationRollsLabel(rolls: Roll[]): string {
    return rolls.map((r) => `${r.number} (${this.getRollLocationLabel(r)})`).join(', ');
  }

  getRollWeightInLbs(roll: Roll): number {
    return KnittingUtil.getRollTimeStamp(roll)?.weightInLbs || 0;
  }

  // ========== DYEING CONFIGURATION ==========

  setDyeingMode(mode: DyeingMode): void {
    this.dyeingMode.set(mode);
    if (mode === 'bundles') {
      this.initializeBundles();
    }
  }

  initializeBundles(): void {
    this.bundles = [
      { id: 'bundle-1', name: 'Bundle 1', rolls: [] },
      { id: 'bundle-2', name: 'Bundle 2', rolls: [] },
    ];

    this.unassignedRolls = this.selectedRolls().map((r) => ({
      rollNumber: r.number || '',
      weight: this.getRollWeightInLbs(r),
    }));

    this.selectedBundleId = this.bundles[0]?.id || null;
  }

  addBundle(): void {
    const newId = `bundle-${this.bundles.length + 1}`;
    this.bundles.push({ id: newId, name: `Bundle ${this.bundles.length + 1}`, rolls: [] });
  }

  removeBundle(bundleId: string): void {
    const bundle = this.bundles.find((b) => b.id === bundleId);
    if (bundle) {
      this.unassignedRolls.push(...bundle.rolls);
      this.bundles = this.bundles.filter((b) => b.id !== bundleId);
      if (this.selectedBundleId === bundleId) {
        this.selectedBundleId = this.bundles[0]?.id || null;
      }
    }
  }

  selectBundle(bundleId: string): void {
    this.selectedBundleId = bundleId;
  }

  addRollToBundle(roll: BundleRoll): void {
    if (!this.selectedBundleId) return;
    const bundle = this.bundles.find((b) => b.id === this.selectedBundleId);
    if (bundle) {
      bundle.rolls.push(roll);
      this.unassignedRolls = this.unassignedRolls.filter((r) => r.rollNumber !== roll.rollNumber);
    }
  }

  removeRollFromBundle(bundleId: string, rollNumber: string): void {
    const bundle = this.bundles.find((b) => b.id === bundleId);
    if (bundle) {
      const roll = bundle.rolls.find((r) => r.rollNumber === rollNumber);
      if (roll) {
        bundle.rolls = bundle.rolls.filter((r) => r.rollNumber !== rollNumber);
        this.unassignedRolls.push(roll);
      }
    }
  }

  getBundleTotalWeight(bundle: BundleItem): number {
    return bundle.rolls.reduce((sum, r) => sum + r.weight, 0);
  }

  get filteredUnassignedRolls(): BundleRoll[] {
    const filter = this.unassignedRollFilter().toLowerCase().trim();
    if (!filter) return this.unassignedRolls;
    return this.unassignedRolls.filter((r) => r.rollNumber.toLowerCase().includes(filter));
  }

  // ========== STEP NAVIGATION ==========

  onStepChange(step?: number): void {
    if (step === undefined || (step > 1 && !this.canProceedToStep2())) return;
    this.activeStep.set(step);
  }

  canProceedToStep2(): boolean {
    return this.selectedProductionCard() !== null && this.selectedRolls().length > 0 && this.selectedInvalidLocationRolls().length === 0;
  }

  canProceedToStep3(): boolean {
    if (!this.includeDyeing()) {
      return true; // Finishing only - no extra validation needed
    }

    // Dyeing validation
    if (!this.selectedDyeingMachine()) {
      return false;
    }

    if (this.dyeingMode() === 'bundles') {
      // All rolls must be assigned to bundles
      // and there must be at least one bundle with rolls
      // and there can't be empty bundles
      const hasAtLeastOneBundleWithRolls = this.bundles.some((b) => b.rolls.length > 0);
      if (!hasAtLeastOneBundleWithRolls) return false;

      const hasEmptyBundles = this.bundles.some((b) => b.rolls.length === 0);
      if (hasEmptyBundles) return false;

      return this.unassignedRolls.length === 0;
    }

    return true;
  }

  // ========== SUBMISSION ==========

  confirmReprocessing(): void {
    const pc = this.selectedProductionCard();
    if (!pc?.productionCardNumber) return;

    this.isSubmitting.set(true);

    const command: ReprocessCommand = {
      productionCardReference: pc.id!,
      rollNumbers: this.selectedRolls().map((r) => r.number!),
      isWholeProductionCard: this.isWholeCardReprocess(),
    };

    if (this.includeDyeing() && this.selectedDyeingMachine()) {
      const dyeingCommand: DyeingReprocessCommand = {
        dyeingMachineAssetId: this.selectedDyeingMachine()!.assetId,
      };

      if (this.dyeingMode() === 'bundles') {
        dyeingCommand.bundleToRollNumbersMap = {};
        this.bundles.forEach((bundle) => {
          dyeingCommand.bundleToRollNumbersMap![bundle.name] = bundle.rolls.map((r) => r.rollNumber);
        });
      }

      command.dyeingReprocessCommand = dyeingCommand;
    }

    this.reprocessProxy
      .reprocessProductionCard(command)
      .pipe(finalize(() => this.isSubmitting.set(false)))
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.reprocessResult.set(response.data);
            this.loadResultProductionCard(response.data);
            this.activeStep.set(4);
          }
        },
      });
  }

  private loadResultProductionCard(result: ReprocessCommandHistory): void {
    const targetPcId = result.newProductionCardReference ?? result.originalProductionCardReference;
    if (!targetPcId) return;

    this.productionCardProxy.getProductionCardByID(targetPcId).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.resultProductionCard.set(response.data);
        }
      },
    });
  }

  // ========== HELPERS ==========

  getSelectedRollCount(): number {
    return this.selectedRolls().length;
  }

  getTotalSelectedWeight(): number {
    return this.selectedRolls().reduce((sum, r) => sum + this.getRollWeightInLbs(r), 0);
  }

  getStatusDisplayName(status: number): string {
    return ProductionCardStatus[status] ?? status.toString();
  }

  resetForm(): void {
    this.activeStep.set(1);
    this.selectedProductionCard.set(null);
    this.rolls.set([]);
    this.selectedRolls.set([]);
    this.workOrder.set(null);
    this.color.set(null);
    this.isWholeCardReprocessControl.setValue(true);
    this.showRollsTable.set(false);
    this.includeDyeingControl.setValue(false);
    this.dyeingMode.set('batching');
    this.selectedDyeingMachineControl.setValue(null);
    this.bundles = [];
    this.unassignedRolls = [];
    this.reprocessResult.set(null);
    this.resultProductionCard.set(null);
  }
}
